import type { components as crocotileComponents } from './generated/Crocotile';
import type {
    components,
    paths as evergladesProductApiPaths,
    components as productApiComponents,
} from './generated/ProductsApi';

export type Availability = crocotileComponents['schemas']['AvailabilityV2'];
export type Badge = crocotileComponents['schemas']['Badge'];
export type Color = crocotileComponents['schemas']['Color'];
export type ColorVariation = [variationId: string, color: Color];
export type Image = crocotileComponents['schemas']['Image'];
export type Price = crocotileComponents['schemas']['Price'];
export type CustomerReviews = crocotileComponents['schemas']['CustomerReviews'];
export type SaleTags = crocotileComponents['schemas']['SaleTags'];
export type SocialProof = crocotileComponents['schemas']['SocialProof'];
export type Variation = crocotileComponents['schemas']['VariationDataViewV2'];
export type Colors = Variation['colors'];
export type EnergyLabel = crocotileComponents['schemas']['EnergyLabel'];
export type TopInfo = crocotileComponents['schemas']['TopInfo'];
export type RadioEquipmentRegulation = crocotileComponents['schemas']['RadioEquipmentRegulation'];
export type InstallmentPlan = crocotileComponents['schemas']['InstallmentPlan'];

export type Banner = {
    tilelistSlot?: number;
    type: 'BT';
};
export type Slider = {
    sliderType?: 'NONE' | 'NEWS' | 'RATING' | 'RATING_BLACK_FRIDAY';
    type: 'SLIDER';
};
export type ProductComparisonBanner = { type: 'PCB' };
export type SimilarProductHeader = { type: 'SPH' };

export type Product = productApiComponents['schemas']['Product'];
export type OrganicProduct = productApiComponents['schemas']['OrganicProduct'];
export type SponsoredProduct = productApiComponents['schemas']['SponsoredProduct'];
export type SimilarProduct = components['schemas']['SimilarProduct'];
export function isOrganicProduct(product: Product): product is OrganicProduct {
    return product.productType === 'organic';
}
export function isSponsoredProduct(product: Product): product is SponsoredProduct {
    return product.productType === 'sponsored';
}
export function isSimilarProduct(product: Product): product is SimilarProduct {
    return product.productType === 'similar';
}
export type IntentType = components['schemas']['IntentType'];
export type ProductType = components['schemas']['ProductType'];

export type EvergladesProductsApiSuccess =
    evergladesProductApiPaths['/products']['get']['responses']['200']['content']['application/json'];
export type EvergladesProductsApiQueryParams = evergladesProductApiPaths['/products']['get']['parameters']['query'];

export type EvergladesProductsApiHeaders = {
    'X-Tracking-Sfid'?: string;
    'x-entry-selection-rule'?: string;
};
export type IntentMeta = components['schemas']['IntentMeta'];
export type IntentResult = components['schemas']['IntentResult'];
export type RankedIntentResult = components['schemas']['RankedIntentResult'];
export function isRankedIntent(intent: IntentResult): intent is RankedIntentResult {
    return intent.intent === 'ranked';
}
export type SponsoredIntentResult = components['schemas']['SponsoredIntentResult'];
export function isSponsoredIntent(intent: IntentResult): intent is SponsoredIntentResult {
    return intent.intent === 'sponsored';
}
export type SimilarIntentResult = components['schemas']['SimilarIntentResult'];
export function isSimilarIntent(intent: IntentResult): intent is SimilarIntentResult {
    return intent.intent === 'similar';
}
export type ContextIntentResult = components['schemas']['ContextIntentResult'];
export function isContextIntent(intent: IntentResult): intent is ContextIntentResult {
    return intent.intent === 'context';
}
export type LayoutIntentResult = components['schemas']['LayoutIntentResult'];
export function isLayoutIntent(intent: IntentResult): intent is LayoutIntentResult {
    return intent.intent === 'layout';
}
export type InterleavedIntentResult = components['schemas']['InterleavedIntentResult'];
export type InterleavedProducts = InterleavedIntentResult['products'];
export function isInterleavedIntent(intent: IntentResult): intent is InterleavedIntentResult {
    return intent.intent === 'interleaved';
}

export type TileImage = crocotileComponents['schemas']['TileImage'];

export type SearchContext = {
    rule: string;
    searchTerm?: string;
    assortment?: string;
    categoryPath?: string;
    pbk?: string;
    explicit?: { [key: string]: string[] };
    implicit?: { [key: string]: string[] };
    topNImplicit?: { [key: string]: string[] };
};

export type Deal = crocotileComponents['schemas']['Deal'];

/**
 *
 */
export type OnColorChangePayload = { variationId: string; amountColors: number; colorPosition: number };

export type Logger = Pick<Console, 'info' | 'warn' | 'error' | 'debug'>;
export type ScopedLogger = Logger & { scope: (name: string) => Logger };
